import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ExploreSearchParams } from "@/routes/explore/index";

import { ClearIcon, GhostIcon, PumpkinIcon } from "./icons";
import OptionsModal from "./options-modal";

export default function ScaryFilter() {
  const [isOpen, setIsOpen] = useState(false);
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleClear = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    const { is_scary: _, ...newSearch } = search;

    navigate({
      search: newSearch,
      to: pathname,
    });
  };

  const { bgGradient, clearButton, initialState, label } = useMemo(() => {
    if (search.is_scary === "Yes") {
      return {
        bgGradient: "from-sangria from-2% via-ginger via-48% to-red to-97%",
        clearButton: true,
        initialState: true,
        label: "Scary! 🔪",
      };
    }

    if (search.is_scary === "No") {
      return {
        bgGradient: "from-forest-green from-2% to-jade to-97%",
        clearButton: true,
        initialState: false,
        label: "Not scary 🐣",
      };
    }

    return {
      bgGradient: "from-bronze from-2% to-golden-yellow to-97%",
      clearButton: false,
      initialState: null,
      label: "Scary?",
    };
  }, [search.is_scary]);

  return (
    <>
      <Button
        className={cn(
          "relative h-7.5 rounded-[15px] bg-linear-54 px-5 font-semibold text-12 text-white",
          bgGradient
        )}
        onClick={() => setIsOpen(true)}
        type="button"
      >
        {label}
        <GhostIcon className="absolute top-2.5 left-2" />
        <PumpkinIcon className="absolute -top-[3px] right-[7px]" />
        {clearButton && (
          // biome-ignore lint/a11y/noStaticElementInteractions: all good
          // biome-ignore lint/a11y/useKeyWithClickEvents: all good
          // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
          <span
            className="absolute -top-px -right-px flex size-2.5 items-center justify-center rounded-full bg-white shadow-0-0-4"
            onClick={handleClear}
          >
            <ClearIcon />
          </span>
        )}
      </Button>
      <OptionsModal
        initialState={initialState}
        onOpenChange={setIsOpen}
        open={isOpen}
      />
    </>
  );
}
